import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, Coins, Sparkle } from "@phosphor-icons/react";

export default function Leaderboard() {
  const [period, setPeriod] = useState("all");
  const [items, setItems] = useState([]);
  const [badges, setBadges] = useState([]);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get(`/leaderboard?period=${period}&limit=20`).then(({ data }) => setItems(data));
  }, [period]);
  useEffect(() => {
    api.get("/badges/me").then(({ data }) => setBadges(data));
    api.get("/badges/catalog").then(({ data }) => setCatalog(data));
  }, []);

  const earned = new Set(badges.map((b) => b.badge_code));

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="leaderboard-page">
      <div className="mb-6 flex items-center gap-3">
        <Trophy size={28} className="text-[#E1252A]" weight="fill" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recognition</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold">Leaderboard</h1>
        </div>
      </div>

      <Tabs defaultValue="board" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="board" data-testid="tab-board">Top contributors</TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges">My badges</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="pt-4 space-y-3">
          <div className="flex gap-2 mb-2">
            {[
              { v: "all", l: "All time" },
              { v: "monthly", l: "This month" },
            ].map((p) => (
              <button
                key={p.v}
                onClick={() => setPeriod(p.v)}
                className={`px-3 py-1 text-xs uppercase tracking-wider rounded-sm border ${period === p.v ? "bg-foreground text-background border-foreground" : "border-border"}`}
                data-testid={`period-${p.v}`}
              >
                {p.l}
              </button>
            ))}
          </div>
          <div className="border border-border bg-background">
            <table className="w-full text-sm" data-testid="leaderboard-table">
              <thead className="border-b border-border">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="text-left py-2 px-3 w-12">Rank</th>
                  <th className="text-left py-2 px-3">Contributor</th>
                  <th className="text-left py-2 px-3 hidden md:table-cell">Role</th>
                  <th className="text-right py-2 px-3">Credits</th>
                  <th className="text-right py-2 px-3">Impact</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.user_id} className="border-b border-border hover:bg-zinc-50 dark:hover:bg-zinc-900" data-testid={`leader-${u.user_id}`}>
                    <td className="py-2 px-3 font-mono font-semibold">#{u.rank}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={u.avatar} />
                          <AvatarFallback className="text-[10px]">{(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-zinc-500 text-xs uppercase tracking-wider hidden md:table-cell">{u.role}</td>
                    <td className="py-2 px-3 text-right font-mono text-[#E1252A] font-semibold">{u.lifetime_earned}</td>
                    <td className="py-2 px-3 text-right font-mono">{u.lifetime_impact}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-12 text-sm text-zinc-500">No leaders yet — complete tasks to climb.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="badges" className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {catalog.map((b) => {
              const got = earned.has(b.code);
              return (
                <div key={b.code} className={`border p-4 ${got ? "border-[#E1252A] bg-[#E1252A]/5" : "border-border bg-background opacity-60"}`} data-testid={`badge-${b.code}`}>
                  <div className="flex items-center gap-2">
                    <Trophy size={18} className={got ? "text-[#E1252A]" : "text-zinc-400"} weight={got ? "fill" : "regular"} />
                    <div className="text-sm font-semibold">{b.name}</div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">{b.description}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] mt-3">{got ? "Earned" : "Locked"}</div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
